import type { LucideIcon } from "lucide-react";
import {
  Bot,
  BriefcaseBusiness,
  Building2,
  ClipboardCheck,
  FileQuestion,
  FileText,
  GraduationCap,
  Handshake,
  ListChecks,
  MessageSquareText,
  PanelTop,
  PlayCircle,
  SearchCheck,
  ShieldQuestion,
  Sparkles,
  Users,
} from "lucide-react";

export const TALK_TO_FOUNDER_URL = "mailto:apex25@theresume.io?subject=theRecruit Demo Hub";

export type DemoStatus = "Available soon" | "Live demo" | "Coming soon";

export type DemoCardConfig = {
  id: string;
  title: string;
  persona: string;
  description: string;
  time: string;
  status: DemoStatus;
  ctaLabel: string;
  icon: LucideIcon;
  url?: string;
};

export type SimpleCardConfig = {
  title: string;
  value?: string;
  description?: string;
  status?: string;
  ctaLabel?: string;
  icon: LucideIcon;
  url?: string;
};

export type HiringLoopStep = {
  title: string;
  text: string;
};

export const demoCards: DemoCardConfig[] = [
  {
    id: "recruiter-workflow",
    title: "Recruiter Workflow Demo",
    persona: "Recruiters & hiring teams",
    description:
      "Create a job, review ranked resumes, shortlist candidates, and move from search to decision faster.",
    time: "4 min",
    status: "Available soon",
    ctaLabel: "Open walkthrough",
    icon: SearchCheck,
    // TODO: Embed interactive demos from Supademo, Navattic, Arcade, or similar here.
  },
  {
    id: "ai-interview",
    title: "AI Interview Demo",
    persona: "Recruiters & interview teams",
    description:
      "See how AI interviews create structured transcripts, communication signals, and candidate insights.",
    time: "3 min",
    status: "Available soon",
    ctaLabel: "View interview flow",
    icon: Bot,
    // TODO: Add the guided AI interview demo URL.
  },
  {
    id: "candidate-experience",
    title: "Candidate Experience Demo",
    persona: "Candidates & colleges",
    description:
      "See how candidates use theResume to improve resumes, prepare for interviews, and share stronger profiles.",
    time: "3 min",
    status: "Coming soon",
    ctaLabel: "Explore candidate side",
    icon: GraduationCap,
    // TODO: Link this page from theResume public site and add candidate-side demo assets.
  },
  {
    id: "decision-workspace",
    title: "Decision Workspace Demo",
    persona: "Hiring managers",
    description:
      "Compare shortlisted candidates using resume fit, interview evidence, transcripts, and recruiter notes.",
    time: "4 min",
    status: "Coming soon",
    ctaLabel: "See decision view",
    icon: ClipboardCheck,
    // TODO: Add a guided decision workspace demo URL.
  },
  {
    id: "staffing-agency",
    title: "Staffing Agency Demo",
    persona: "Staffing & recruitment agencies",
    description:
      "Turn old and new candidate databases into a searchable, matchable, interview-ready talent engine.",
    time: "5 min",
    status: "Coming soon",
    ctaLabel: "Explore agency flow",
    icon: Building2,
    // TODO: Add prospect-specific demo rooms later.
  },
];

export const hiringLoopSteps: HiringLoopStep[] = [
  {
    title: "Create or import a job",
    text: "Start with the role, job description, location, experience range, and skills.",
  },
  {
    title: "Rank resumes against the job",
    text: "theRecruit compares candidates using job relevance, skills, experience, and AI-assisted matching.",
  },
  {
    title: "Send AI interviews",
    text: "Shortlisted candidates receive structured AI interviews connected to the role.",
  },
  {
    title: "Capture deeper hiring signals",
    text: "Interview transcripts, response quality, communication signals, and structured insights are stored for review.",
  },
  {
    title: "Decide with evidence",
    text: "Recruiters and hiring managers compare candidates with more than just resume keywords.",
  },
];

export const personaCards: SimpleCardConfig[] = [
  {
    title: "Recruiters",
    value: "Reduce manual screening, shortlist faster, and interview more candidates consistently.",
    icon: Users,
  },
  {
    title: "Staffing agencies",
    value:
      "Revive candidate databases and match existing talent to new roles with better speed and structure.",
    icon: BriefcaseBusiness,
  },
  {
    title: "Colleges & training institutes",
    value:
      "Prepare students for real hiring workflows with resumes, AI interviews, and employer-ready profiles.",
    icon: GraduationCap,
  },
  {
    title: "Hiring managers",
    value:
      "Review evidence-backed candidate comparisons instead of relying only on recruiter summaries.",
    icon: ClipboardCheck,
  },
  {
    title: "Candidates",
    value:
      "Improve resumes, prepare for interviews, and become more discoverable through theResume.",
    icon: Sparkles,
  },
];

export const outcomeCards: SimpleCardConfig[] = [
  { title: "Which candidates fit this role best?", icon: SearchCheck },
  { title: "Why are they ranked higher?", icon: ListChecks },
  { title: "Who completed the interview?", icon: MessageSquareText },
  { title: "What did the candidate actually say?", icon: FileText },
  { title: "What are the risks, gaps, and strengths?", icon: ShieldQuestion },
  { title: "Who should move to the next round?", icon: Handshake },
];

export const resourceCards: SimpleCardConfig[] = [
  {
    title: "90-sec overview video",
    description: "A short visual explanation of the hiring intelligence loop.",
    status: "Coming soon",
    ctaLabel: "Watch video",
    icon: PlayCircle,
    // TODO: Replace placeholder video with actual 90-sec product video.
  },
  {
    title: "One-page product brief",
    description: "A forwardable summary for internal stakeholders.",
    status: "Coming soon",
    ctaLabel: "Download PDF",
    icon: FileText,
    // TODO: Add downloadable one-pager PDF.
  },
  {
    title: "ROI calculator",
    description: "Estimate time saved, screening cost reduced, and placement value unlocked.",
    status: "Coming soon",
    ctaLabel: "Calculate ROI",
    icon: PanelTop,
    // TODO: Connect ROI calculator.
  },
  {
    title: "Security & implementation FAQ",
    description: "Answers for data privacy, deployment, integrations, and enterprise review.",
    status: "Coming soon",
    ctaLabel: "Read FAQ",
    icon: FileQuestion,
    // TODO: Add security and implementation FAQ.
  },
];
